import React from "react";
import { Link } from "react-router-dom";
import { logos } from "./data";
import { brands } from "./brands";
import { colors, fonts } from "./theme";

const slugFor = (name) => {
  const b = brands.find((x) => x.name === name);
  return b ? b.slug : null;
};

// One full run of the logos. Rendered twice inside the track so the
// animation (translateX -50%) loops seamlessly.
//
// Each <img> carries its intrinsic width/height. The CSS pins the height to
// 40px and leaves the width auto, so without those attributes the browser has
// no aspect ratio to work from and every logo occupies zero width until it
// decodes — the whole strip snaps sideways as the files land. With them the
// row is laid out correctly on first paint.
//
// Only the first row links. The duplicate exists purely so the marquee can
// loop: it is aria-hidden, its images take alt="", and it renders plain divs
// rather than anchors so the page does not carry twelve duplicate links to the
// same twelve targets.
function LogoRow({ hidden }) {
  return (
    <div className="se-logos-group" aria-hidden={hidden || undefined}>
      {logos.map((logo, i) => {
        const img = (
          <img
            src={logo.src}
            alt={hidden ? "" : `${logo.name} logo`}
            width={logo.w}
            height={logo.h}
            loading="lazy"
            decoding="async"
            style={{ height: "40px", width: "auto", maxWidth: "130px", objectFit: "contain", display: "block" }}
          />
        );
        const slug = hidden ? null : slugFor(logo.name);
        return (
          <div
            key={i}
            className="se-logo-cell"
            style={{ flex: "0 0 auto", display: "grid", placeItems: "center" }}
          >
            {slug ? (
              <Link
                to={`/brands#${slug}`}
                title={`${logo.name} paper stocked by Sigma Enterprises`}
                // The gutter lives on the anchor rather than on the cell so it
                // counts as hit area. As cell padding it was dead space around
                // a 40px-tall target; here it makes the target 64px tall and
                // the full cell wide.
                style={{ display: "grid", placeItems: "center", minHeight: "64px", padding: "0 var(--se-logo-gap)", cursor: "pointer" }}
              >
                {img}
              </Link>
            ) : (
              <span style={{ display: "grid", placeItems: "center", minHeight: "64px", padding: "0 var(--se-logo-gap)" }}>{img}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// White band with an infinitely scrolling strip of the brands we stock.
//
// This strip once carried the brand names only inside alt attributes, which is
// a weak signal, so the site said nothing indexable about stocking JK Paper or
// Avery Dennison. A plain-text list of all twelve was added to fix that, and it
// worked but read as clutter under the logos.
//
// It is gone again, and nothing is hidden to compensate: text hidden with CSS
// for search engines is cloaking under Google's spam policies, and hidden text
// is discounted anyway, so it would carry the risk without the benefit.
//
// The signal now comes from real structure instead. /brands carries all twelve
// with full visible copy, and each logo here is a crawlable internal link into
// its entry on that page — which is worth more than a list of bare names ever
// was, because the anchor points at a page that actually answers the query.
export default function LogoMarquee() {
  return (
    <section
      style={{
        background: "#FFFFFF",
        borderTop: `1px solid ${colors.border}`,
        borderBottom: `1px solid ${colors.border}`,
        padding: "clamp(24px,6vw,32px) 0 clamp(22px,5.5vw,30px)",
      }}
    >
      <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "0 var(--sp-gutter)" }}>
        <h2
          style={{
            fontFamily: fonts.mono,
            fontSize: "var(--fs-label)",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: colors.brown,
            margin: "0 0 22px",
            textAlign: "center",
            fontWeight: 500,
          }}
        >
          Mill brands we stock
        </h2>
      </div>

      <div className="se-logos" style={{ padding: "2px 0 0" }}>
        <div className="se-logos-track">
          <LogoRow />
          <LogoRow hidden />
        </div>
      </div>

      <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "14px var(--sp-gutter) 0", textAlign: "center" }}>
        <Link
          to="/brands"
          // inline-flex + min-height turns a 19px-tall run of text into a
          // 44px target. The underline moves to box-shadow because a
          // border-bottom on an inline box is redrawn on every wrapped line
          // fragment, and this label does wrap below ~360px.
          style={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: "44px",
            padding: "0 4px",
            fontSize: "var(--fs-meta)",
            color: colors.brown,
            cursor: "pointer",
            boxShadow: "inset 0 -1px 0 #C9B896",
          }}
        >
          All brands and what we stock from each →
        </Link>
      </div>
    </section>
  );
}
